import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { CameraService } from 'src/app/engine/camera.service';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { Observable, Subject, interval, Subscription, animationFrame } from 'rxjs';
import { map, takeUntil, filter, tap, distinctUntilChanged, delay } from 'rxjs/operators';
import { WidgetOptions } from '../../models/widget-options.model';
import { ScreenDimensions } from 'src/app/shared/models/screen.dimensions.model';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { MessageEnum } from 'src/app/widget-api/models/message.enum';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { UiIO } from 'src/app/widget-api/models/ui.io';
import { InteractTypeEnum } from 'src/app/view/models/interact-type.enum';
import { ScrollModeEnum } from 'src/app/engine/models/scroll-mode.enum';
import { QuizIO } from 'src/app/widget-api/models/quiz.io';
import { ViewQuizModeEnum } from 'src/app/view/models/view-quiz-mode.enum';
import { get, isEqual, isNil } from 'lodash';

declare const HumanAPI: any;

const scaleSmoothFactor = 0.95;

export interface PublishWidgetOptions {
  width: number;
  height: number;
  scalable: boolean;
  labelScale: number;
  options: WidgetOptions;
  backgroundColor: BackgroundModel;
}

@Component({
  selector: 'app-publish-widget',
  templateUrl: './publish-widget.component.html',
  styleUrls: ['./publish-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublishWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('widgetContainer', { static: true })
  containerRef: ElementRef;

  @ViewChild('widgetFrame', { static: true })
  frameRef: ElementRef;

  @Input()
  model: BookmarkModel;

  @Input()
  public get options(): PublishWidgetOptions {
    return this._options;
  }
  public set options(value: PublishWidgetOptions) {
    if (value) {
      this._options = value;
      this.updateWidget(this.options);
    }
  }
  private _options: PublishWidgetOptions;

  width: number;
  height: number;
  scalable = false;
  labelScale = 1;

  defaultBackgroundColor: BackgroundModel;
  backgroundColor: BackgroundModel;

  allVisibilityLayers = [];

  @Output()
  cameraUpdated = new EventEmitter<CameraModel>();

  private ngUnsubscribe = new Subject();
  private widgetOptionsSubject = new Subject<WidgetOptions>();

  get topOffset(): number {
    const containerHeight = this.elRef.nativeElement.offsetHeight;
    const targetOffset = (containerHeight - this.height) / 2;
    return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
  }

  get leftOffset(): number {
    const containerWidth = this.elRef.nativeElement.offsetWidth;
    const targetOffset = (containerWidth - this.width) / 2;
    return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
  }

  get scaleFactor(): number {
    if (!this.scalable) {
      return 1;
    } else {
      const { offsetWidth, offsetHeight } = this.containerRef.nativeElement;
      const xScale = offsetWidth / this.width;
      const yScale = offsetHeight / this.height;
      const targetScale = Math.min(xScale, yScale, 1);

      if (targetScale === 1) {
        // default scale
        return 1;
      } else {
        // scaled model: smooth
        return targetScale * scaleSmoothFactor;
      }
    }
  }

  private _apiRef: any;
  private _params: WidgetOptions;
  private _defaultCamera: CameraModel;
  private _currentCamera: CameraModel;
  private _rotateDegrees: number;

  constructor(
    private elRef: ElementRef,
    private cameraService: CameraService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    // set API instance
    const frameId = this.frameRef.nativeElement.id;
    this._apiRef = new HumanAPI({ iframeId: frameId });

    // init API hooks
    this.initAPIListeners();
    this.initQuizAPIListeners();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initAPIListeners() {
    // listen for API ready state
    this.getAPIEvent(EventsEnum.HumanReady)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        // ready state
        this.setupCameraTracking();
        this.setupCameraRotation();
        this.setWidgetUIConfig(this._params);

        // setup background color
        this.getModelBackgroundColor()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((modelColor) => {
            this.defaultBackgroundColor = modelColor;
            this.setWidgetBackground(this.options.backgroundColor || this.defaultBackgroundColor);
          });

        this.getLayers()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((allVisibilityLayers) => {
            this.allVisibilityLayers = allVisibilityLayers;
            this.changeDetectorRef.markForCheck();
          });
      });
  }

  initQuizAPIListeners() {
    // listen for quiz updates
    const uiQuiz$ = this.widgetOptionsSubject.asObservable().pipe(
      map((widgetOptions) => widgetOptions.uiQuiz),
      filter((uiQuiz) => !isNil(uiQuiz)),
    );

    uiQuiz$
      .pipe(
        distinctUntilChanged((a, b) => isEqual(a, b)),
        filter(() => !isNil(this._apiRef)),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((uiQuiz) => {
        const { mode, number } = uiQuiz;
        const locked = mode === ViewQuizModeEnum.Locked;

        if (locked) {
          // restart quiz w/ config
          const questionNumber = parseInt(number, 10);
          const startNumber = !isNaN(questionNumber) ? questionNumber : 1;
          const endNumber = !isNaN(questionNumber) ? questionNumber : undefined;

          this._apiRef.send(MessageEnum.QuizReset);
          this._apiRef.send(MessageEnum.QuizSet, {
            locked,
            startNumber,
            endNumber,
          });
          this._apiRef.send(MessageEnum.QuizEnter);
        } else {
          // end quiz
          this._apiRef.send(MessageEnum.QuizSet, { locked });
          this._apiRef.send(MessageEnum.QuizExit);
        }
      });
  }

  updateWidget(publishWidgetOptions: PublishWidgetOptions) {
    this.width = publishWidgetOptions.width;
    this.height = publishWidgetOptions.height;
    this.scalable = publishWidgetOptions.scalable;

    this.setLabelScale(publishWidgetOptions.labelScale);
    this.setWidgetUIConfig(publishWidgetOptions.options);
    this.setWidgetBackground(this.options.backgroundColor || this.defaultBackgroundColor);
  }

  setWidgetUIConfig(widgetOptions: WidgetOptions) {
    // API ready
    if (this._apiRef && widgetOptions) {
      const uiDisplay = this.convertWidgetOptionsToDisplayParams(widgetOptions);
      this.setUIDisplay(uiDisplay);

      const onDemandExperience: UiIO.UISetOndemandExperienceRequest = {
        enabled: !widgetOptions.autoLoad,
      };

      const loadingExperience = ((initial) => {
        if (initial.button) {
          return InteractTypeEnum.Button;
        }
        if (initial.handHint) {
          return InteractTypeEnum.Hand;
        }
        if (initial.play) {
          return InteractTypeEnum.Play;
        }
        return undefined;
      })(widgetOptions.initial);

      const scrollCaptureMode = ((disableScroll) => {
        if (disableScroll.alert) {
          return ScrollModeEnum.DoNotCaptureShowMessage;
        }
        if (disableScroll.overlay) {
          return ScrollModeEnum.DoNotCapture;
        }
        return ScrollModeEnum.Capture;
      })(widgetOptions.disableScroll);

      const setTutorialRequest: UiIO.UISetTutorialRequest = {
        enabled: widgetOptions.uiTutorial,
      };

      this._apiRef.send(MessageEnum.UISetOndemand, onDemandExperience);
      this._apiRef.send(MessageEnum.UISetLoadingExperience, loadingExperience);
      this._apiRef.send(MessageEnum.UISetScrollCaptureMode, scrollCaptureMode);
      this._apiRef.send(MessageEnum.UISetTutorial, setTutorialRequest);

      // publish options update
      this.widgetOptionsSubject.next(widgetOptions);
    }
  }

  takeSnapshot(
    fileType: 'png' | 'jpeg' = 'jpeg',
    dimensions?: ScreenDimensions,
  ): Observable<string> {
    return this.getAPIData(MessageEnum.UISnapshot, {
      width: this.width,
      height: this.height,
      fileType,
      ...dimensions,
    });
  }

  /**
   * Orbits the camera around the model by specified degrees.
   */
  private setCameraOrbit(degrees: number) {
    this.sendAPIData(MessageEnum.CameraOrbit, {
      yaw: degrees,
    });
  }

  /**
   * Sets UI options (i.e., fullscreen, help, etc...)
   */
  private setWidgetBackground(background: BackgroundModel) {
    if (this._apiRef && background) {
      this.sendAPIData(MessageEnum.UIPresetBackground, background);
    }
  }

  private setLabelScale(scale: number) {
    if (this._apiRef) {
      this.sendAPIData(MessageEnum.LabelsSetScale, {
        scale: scale,
      });
    }
  }

  private setupCameraTracking() {
    // get initial camera
    this.getCurrentCamera()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((currentCamera) => {
        // set fixed default camera
        this.setDefaultCamera(currentCamera);

        // begin tracking camera changes
        this.getCameraUpdates()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((updateCamera) => {
            this.setUpdateCamera(updateCamera);
          });
      });
  }

  // use API to rotate widget fixed amount in an interval
  private setupCameraRotation() {
    // setup camera rotate animation
    let timeLast = performance.now();
    interval(0, animationFrame)
      .pipe(
        filter(() => !!this._rotateDegrees), // on valid rotation values
        map(() => this._rotateDegrees * (performance.now() - timeLast) * 0.001),
        tap(() => (timeLast = performance.now())),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((degrees) => {
        this.setCameraOrbit(degrees);
      });
  }

  private setDefaultCamera(camera: CameraModel) {
    this._defaultCamera = camera;
    this.setUpdateCamera(camera);
  }

  private setUpdateCamera(camera: CameraModel) {
    this._currentCamera = camera;

    const camerasMatch = this.cameraService.areCamerasEqual(
      this.cameraService.roundCamera(this._defaultCamera, 3),
      this.cameraService.roundCamera(this._currentCamera, 3),
    );

    // dispatch changes: validate delta from original
    const targetCamera = !camerasMatch ? this._currentCamera : null;
    this.cameraUpdated.emit(targetCamera);
  }

  private getAPIEvent(event: EventsEnum): Observable<any> {
    return new Observable<any>((observer) => {
      this._apiRef.on(event, (data: any) => {
        observer.next(data);
      });
    });
  }

  private getAPIData(message: MessageEnum, payload?: any): Observable<any> {
    return new Observable<any>((observer) => {
      this._apiRef.send(message, payload, (data: any) => {
        observer.next(data);
        observer.complete();
      });
    });
  }

  private sendAPIData(message: MessageEnum, payload?: any) {
    this._apiRef.send(message, payload);
  }

  private getCurrentCamera(): Observable<CameraModel> {
    return this.getAPIData(MessageEnum.CameraInfo).pipe(
      map((data: any) => {
        return <CameraModel>{
          eye: data.position,
          look: data.target,
          up: data.up,
        };
      }),
    );
  }

  private getModelBackgroundColor(): Observable<BackgroundModel> {
    return this.getAPIData(MessageEnum.UIGetBackground).pipe(
      map((data) => {
        if (data) {
          return new BackgroundModel(
            [...[].concat(...data.colors)], // [[r,g,b,a], [r,g,b,a]] -> [r,g,b,a,r,g,b,a]
            data.gradientType,
            data.colorStops,
          );
        } else {
          return null;
        }
      }),
    );
  }

  private getCameraUpdates(): Observable<CameraModel> {
    return this.getAPIEvent(EventsEnum.CameraUpdated).pipe(
      map((data) => {
        const { position, target, up } = data;
        return <CameraModel>{
          eye: position,
          look: target,
          up: up,
        };
      }),
    );
  }

  private setUIDisplay(uiDisplay: UiIO.UIDisplayParams) {
    this._apiRef.send(MessageEnum.UISetDisplay, uiDisplay);
  }

  private getLayers() {
    return this.getAPIData(MessageEnum.SceneGetLayers);
  }

  private convertWidgetOptionsToDisplayParams(params: WidgetOptions): UiIO.UIDisplayParams {
    return {
      info: params.uiInfo,
      search: params.uiSearch,
      undo: params.uiUndo,
      fullscreen: params.uiFullscreen,
      nav: params.uiNav,
      tools: params.uiTools,
      help: params.uiHelp,
      chapterList: params.uiChapterList,
      labelList: params.uiLabelList,
      anatomyDescriptions: params.uiAnatomyDescriptions,
      legend: params.uiLegend,
      loader: params.uiLoader,
      whiteboard: params.uiWhiteboard,
      visibilityLayers: params.uiVisibilityLayers,
      anatomyLabels: params.uiAnatomyLabels,
    };
  }
}
